import React, { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ratingColors, ratingLabels, formatDate } from "./constants";
import { ExtractObligationsButton } from "../contract-detail/ExtractObligationsButton";

export function PerformanceTab({ contractId, performanceLogs, fetchData, expanded, canLogPerformance, submitForm }) {
  const [form, setForm] = useState({ category: "delivery", rating: "good", description: "" });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await submitForm(
      `/contracts/${contractId}/performance-logs`,
      form,
      () => setForm({ category: "delivery", rating: "good", description: "" }),
      setShowForm
    );
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{performanceLogs.length} log(s)</p>
        {canLogPerformance && (
          <Button size="sm" variant="outline" className="gap-1 rounded-sm" onClick={() => setShowForm(true)} data-testid="add-performance-btn"><Plus size={14} />Log Performance</Button>
        )}
      </div>

      {showForm && (
        <Card className="border-cyan-200">
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["delivery", "quality", "communication", "compliance", "relationship"].map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rating</Label>
                <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ratingLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm" placeholder="Describe the performance observation..." data-testid="perf-description-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" disabled={saving || !form.description} onClick={handleSave} data-testid="save-performance-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`space-y-2 overflow-y-auto ${expanded ? "max-h-[calc(100vh-350px)]" : "max-h-[400px]"}`}>
        {performanceLogs.map((log) => (
          <div key={log.id} className="flex items-start gap-3 p-3 border rounded-sm">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`rounded-sm text-xs ${ratingColors[log.rating]}`}>{ratingLabels[log.rating]}</Badge>
                <Badge variant="outline" className="text-xs">{log.category}</Badge>
              </div>
              <p className="text-sm">{log.description}</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-slate-400">{log.logged_by_name} · {formatDate(log.created_at)}</p>
                <ExtractObligationsButton
                  eventType="performance_log"
                  eventId={log.id}
                  contractId={contractId}
                  label="Extract Obligations"
                  className="h-6 text-[11px] ml-2"
                />
              </div>
            </div>
          </div>
        ))}
        {performanceLogs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No performance logs yet</p>}
      </div>
    </div>
  );
}
